<template>
  <div class="payment-step">
    <div class="step-badge">
      <i class="el-icon-s-order"></i>
      <span>{{ $t('buy.steps.badge.step6') }}</span>
    </div>

    <div v-if="allowSubscription" class="payment-mode-switch">
      <div
        class="mode-card"
        :class="{ active: paymentMode === 'one_time' }"
        @click="switchPaymentMode('one_time')"
      >
        <div class="mode-top">
          <span class="mode-title">{{ $t('buy.payment.modes.oneTime.title') }}</span>
          <el-tag size="mini" type="info" effect="plain">{{ $t('buy.payment.modes.oneTime.badge') }}</el-tag>
        </div>
        <p class="mode-desc">{{ $t('buy.payment.modes.oneTime.desc') }}</p>
      </div>
      <div
        class="mode-card"
        :class="{ active: paymentMode === 'subscription' }"
        @click="switchPaymentMode('subscription')"
      >
        <div class="mode-top">
          <span class="mode-title">{{ $t('buy.payment.modes.subscription.title') }}</span>
          <el-tag size="mini" type="success" effect="plain">{{ $t('buy.payment.modes.subscription.badge') }}</el-tag>
        </div>
        <p class="mode-desc">{{ $t('buy.payment.modes.subscription.desc') }}</p>
      </div>
    </div>

    <div class="payment-inner-steps">
      <div 
        v-for="(s, idx) in innerSteps" 
        :key="idx"
        class="inner-step"
        :class="{ active: paymentPhase === idx, done: paymentPhase > idx }"
      >
        <div class="inner-step-dot">
          <i v-if="paymentPhase > idx" class="el-icon-check"></i>
          <span v-else>{{ idx + 1 }}</span>
        </div>
        <span class="inner-step-label">{{ $t(`buy.payment.innerSteps.${idx}`) }}</span>
      </div>
    </div>

    <!-- Phase 0: 选择支付方式 -->
    <transition name="pay-fade" mode="out-in">
      <div v-if="paymentPhase === 0" key="select" class="payment-phase">
        <div class="phase-header">
          <h3>{{ phaseTitle }}</h3>
          <p class="phase-desc">{{ phaseDescription }}</p>
          <div class="amount-banner">
            <span class="amount-label">{{ isSubscriptionMode ? $t('buy.payment.subscription.amountLabel') : $t('buy.payment.amountToPay') }}:</span>
            <span class="amount-value">{{ displayCurrency }} {{ formattedFinalAmount }}</span>
          </div>
        </div>

        <div v-if="!isSubscriptionMode" class="methods-grid">
          <div 
            v-for="method in availablePaymentMethods"
            :key="method.key"
            class="method-card"
            :class="[{ selected: selectedMethod === method.key }, `payment-method-${method.key}`]"
            @click="selectedMethod = method.key"
          >
            <div class="method-radio">
              <div class="radio-dot" :class="{ checked: selectedMethod === method.key }"></div>
            </div>
            <div class="method-logo" :class="method.logoClass">
              <img v-if="method.image" :src="method.image" :alt="method.label" />
              <div v-else-if="method.key === 'card'" class="card-network-stack" aria-hidden="true">
                <span class="network-mark visa-mark">VISA</span>
                <span class="network-mark mastercard-mark">
                  <span class="mc-disc mc-red"></span>
                  <span class="mc-disc mc-yellow"></span>
                </span>
              </div>
              <div v-else class="visa-text">{{ method.logoText }}</div>
            </div>
            <div class="method-info">
              <div class="method-title-row">
                <h4>{{ method.label }}</h4>
                <span class="method-channel">{{ method.badge }}</span>
              </div>
              <p>{{ method.desc }}</p>
              <div class="method-meta">
                <span>
                  <em>{{ $t('buy.payment.methodMeta.processor') }}</em>
                  {{ method.processor }}
                </span>
                <span>
                  <em>{{ $t('buy.payment.methodMeta.flow') }}</em>
                  {{ method.flow }}
                </span>
              </div>
            </div>
            <div class="method-badge" v-if="method.recommended">
              <el-tag size="mini" effect="plain" type="success">{{ $t('buy.payment.recommended') }}</el-tag>
            </div>
          </div>
        </div>

        <div v-if="!isSubscriptionMode && selectedMethod === 'alipay'" class="alipay-mode-panel">
          <div class="alipay-mode-header">
            <span>{{ $t('buy.payment.alipayMode.title') }}</span>
            <small>{{ $t('buy.payment.alipayMode.subtitle') }}</small>
          </div>
          <div class="alipay-mode-options">
            <button
              type="button"
              class="alipay-mode-option"
              :class="{ active: alipayPaymentType === 'redirect' }"
              @click="alipayPaymentType = 'redirect'"
            >
              <i class="el-icon-position"></i>
              <span>{{ $t('buy.payment.alipayMode.redirect.title') }}</span>
              <em>{{ $t('buy.payment.alipayMode.redirect.desc') }}</em>
            </button>
            <button
              type="button"
              class="alipay-mode-option"
              :class="{ active: alipayPaymentType === 'qr' }"
              @click="alipayPaymentType = 'qr'"
            >
              <i class="el-icon-full-screen"></i>
              <span>{{ $t('buy.payment.alipayMode.qr.title') }}</span>
              <em>{{ $t('buy.payment.alipayMode.qr.desc') }}</em>
            </button>
          </div>
        </div>

        <div v-if="isSubscriptionMode" class="subscription-panel">
          <div class="subscription-card">
            <div class="subscription-card-header">
              <i class="el-icon-refresh-right"></i>
              <span>{{ $t('buy.payment.subscription.cardTitle') }}</span>
            </div>
            <div class="subscription-card-body">
              <div class="subscription-row">
                <span>{{ $t('buy.payment.subscription.interval') }}</span>
                <strong>{{ orderData && orderData.billingCycle === 'two_year' ? $t('buy.summary.year2') : $t('buy.summary.year1') }}</strong>
              </div>
              <div class="subscription-row">
                <span>{{ $t('buy.payment.subscription.renewal') }}</span>
                <strong>{{ $t('buy.payment.subscription.autoRenew') }}</strong>
              </div>
              <div class="subscription-row">
                <span>{{ $t('buy.payment.subscription.paymentMethod') }}</span>
                <strong>{{ $t('buy.payment.subscription.cardOnly') }}</strong>
              </div>
            </div>
          </div>
        </div>

        <div class="phase-actions">
          <el-button
            type="primary"
            @click="proceedToPayment"
            :disabled="!isSubscriptionMode && !selectedMethod"
            :loading="isSubscriptionMode ? subscriptionLoading : paying"
            class="proceed-btn"
          >
            {{ actionLabel }}
            <i class="el-icon-arrow-right"></i>
          </el-button>
        </div>
      </div>

      <!-- Phase 1: 支付操作 -->
      <div v-else-if="paymentPhase === 1" key="pay" class="payment-phase">
        <!-- 支付宝扫码 / 跳转 -->
        <div v-if="selectedMethod === 'alipay'" class="qr-payment">
          <div class="qr-header">
            <img src="@/assets/img/zfb.png" alt="Alipay" class="qr-method-icon" />
            <h3>{{ alipayPaymentType === 'qr' ? $t('buy.payment.alipayMode.qr.title') : $t('buy.payment.alipayMode.redirect.title') }}</h3>
          </div>
          <div v-if="alipayPaymentType === 'qr'" class="qr-body">
            <div class="qr-checkout-panel">
              <div v-if="orderNumber" class="qr-order-no">
                <span>{{ $t('buy.payment.alipayMode.qr.orderNo') }}</span>
                <strong>{{ orderNumber }}</strong>
              </div>
              <div class="qr-code-stage">
                <div v-if="alipayQrCode" class="qr-code-wrapper alipay-qr-wrapper">
                  <div ref="alipayQrCode" class="alipay-qr-code"></div>
                  <div class="scan-line"></div>
                </div>
                <div v-else class="qr-code-container">
                  <p class="qr-tip">{{ $t('buy.payment.alipayMode.qr.generating') }}</p>
                </div>
              </div>
              <p class="qr-tip">{{ $t('buy.payment.alipayMode.qr.tip') }}</p>
            </div>
          </div>
          <div v-else class="qr-body">
            <p class="qr-tip">{{ $t('buy.payment.alipayMode.redirect.tip') }}</p>
          </div>
          <div class="qr-footer">
            <el-button
              v-if="alipayPaymentType === 'redirect'"
              @click="redirectToAlipay"
              type="primary"
              :loading="paying"
              class="simulate-btn"
            >
              <i class="el-icon-check" v-if="!paying"></i>
              {{ paying ? $t('buy.payment.verifying') : $t('buy.payment.alipayMode.redirect.action') }}
            </el-button>
            <el-button
              v-else
              @click="checkAlipayPaymentStatus"
              type="primary"
              :loading="paying"
              class="simulate-btn"
            >
              <i class="el-icon-refresh" v-if="!paying"></i>
              {{ paying ? $t('buy.payment.verifying') : $t('buy.payment.alipayMode.qr.checkStatus') }}
            </el-button>
            <el-button @click="returnToPaymentMethods" class="back-btn">
              {{ $t('buy.payment.changeMethod') }}
            </el-button>
          </div>
        </div>

        <!-- WeChat Pay 扫码支付 -->
        <div v-else-if="selectedMethod === 'wechat'" class="qr-payment wechat-payment">
          <div class="qr-header">
              <span class="wechat-icon-qr">WeChat</span>
            <h3>{{ $t('buy.payment.methods.wechat.name') }}</h3>
          </div>
          <div v-if="wechatQrUrl" class="qr-code-container">
            <div ref="wechatQrCode" id="wechat-q-code" class="wechat-qr-code"></div>
            <p class="qr-tip">{{ $t('buy.payment.wechat.scanQrCode') }}</p>
          </div>
          <div class="qr-code-container" v-else>
            <p class="qr-tip">{{ $t('buy.payment.wechat.generating') }}</p>
          </div>
          <div class="qr-footer">
            <el-button v-if="wechatQrUrl" @click="returnToPaymentMethods" class="back-btn">
              {{ $t('buy.payment.changeMethod') }}
            </el-button>
          </div>
        </div>

        <!-- Visa / Mastercard 卡片表单 -->
        <div v-else class="card-payment">
          <div class="card-header">
            <div class="card-method-icon">
              <span class="network-mark visa-mark">VISA</span>
              <span class="network-mark mastercard-mark">
                <span class="mc-disc mc-red"></span>
                <span class="mc-disc mc-yellow"></span>
              </span>
            </div>
            <h3>{{ $t(`buy.payment.methods.${selectedMethod}.name`) }}</h3>
          </div>

          <div class="card-form-wrapper">
            <div class="card-checkout-shell">
              <div class="card-checkout-top">
                <div>
                  <span class="card-checkout-kicker">{{ $t('buy.payment.cardForm.kicker') }}</span>
                  <h4>{{ $t('buy.payment.cardForm.title') }}</h4>
                </div>
                <div class="accepted-card-list" :aria-label="$t('buy.payment.cardForm.acceptedCards')">
                  <span class="brand-pill visa-pill">VISA</span>
                  <span class="brand-pill mastercard-pill">
                    <span class="mc-disc mc-red"></span>
                    <span class="mc-disc mc-yellow"></span>
                  </span>
                  <span class="brand-pill">{{ $t('buy.payment.cardForm.moreCards') }}</span>
                </div>
              </div>
              <div class="card-amount-strip">
                <span>{{ $t('buy.payment.amountToPay') }}</span>
                <strong>{{ formattedAmount }}</strong>
              </div>
              <div class="card-input-label">{{ $t('buy.payment.cardForm.cardDetails') }}</div>
              <div id="payment-element" class="stripe-payment-element"></div>
              <p class="card-form-helper">{{ $t('buy.payment.cardForm.helper') }}</p>
            </div>

            <div class="card-actions">
              <el-button 
                type="primary" 
                @click="handleCardPayment" 
                :loading="paying"
                class="pay-now-btn"
              >
                <i class="el-icon-lock" v-if="!paying"></i>
                {{ paying ? $t('buy.payment.processing') : $t('buy.payment.payNow', { amount: formattedAmount }) }}
              </el-button>
              <el-button @click="paymentPhase = 0" class="back-btn">
                {{ $t('buy.payment.changeMethod') }}
              </el-button>
            </div>
          </div>

          <div class="card-security-note">
            <i class="el-icon-lock"></i>
            <span>{{ $t('buy.payment.securityNote') }}</span>
          </div>
        </div>
      </div>

      <!-- Phase 2: 支付结果 -->
      <div v-else-if="paymentPhase === 2" key="result" class="payment-phase">
        <div class="payment-result" :class="paymentSuccess ? 'success' : 'failed'">
          <div class="result-icon-wrapper" :class="paymentSuccess ? 'success-icon' : 'failed-icon'">
            <i :class="paymentSuccess ? 'el-icon-check' : 'el-icon-close'"></i>
          </div>
          <h2 class="result-title">
            {{ paymentSuccess ? resultTitle : $t('buy.payment.result.failedTitle') }}
          </h2>
          <p class="result-subtitle">
            {{ paymentSuccess ? resultDescription : $t('buy.payment.result.failedDesc') }}
          </p>
          
          <div v-if="paymentSuccess" class="result-details">
            <div class="result-detail-row">
              <span>{{ $t('buy.payment.result.orderId') }}</span>
              <span class="detail-value">{{ brandPrefix }}-{{ orderNumber }}</span>
            </div>
            <div class="result-detail-row">
              <span>{{ $t('buy.payment.result.amount') }}</span>
              <span class="detail-value">{{ resultCurrency }}{{ formattedFinalAmount }}</span>
            </div>
            <div class="result-detail-row">
              <span>{{ $t('buy.payment.result.method') }}</span>
              <span class="detail-value">{{ resultMethodLabel }}</span>
            </div>
            <div class="result-detail-row">
              <span>{{ $t('buy.payment.result.time') }}</span>
              <span class="detail-value">{{ paymentTime }}</span>
            </div>
          </div>

          <div class="result-actions">
            <template v-if="paymentSuccess">
              <el-button 
                v-if="!isSubscriptionMode"
                type="primary"
                plain
                @click="downloadPageShortcut"
                class="shortcut-btn"
              >
                <i class="el-icon-download"></i>
                {{ $t('buy.payment.result.downloadShortcut') }}
              </el-button>
              <el-button 
                type="primary" 
                @click="viewInvoice"
                class="invoice-btn"
              >
                <i class="el-icon-document"></i>
                {{ $t('buy.payment.result.viewInvoice') }}
              </el-button>
            </template>
            <el-button 
              v-else
              type="primary" 
              @click="retryPayment"
              class="retry-btn"
            >
              <i class="el-icon-refresh"></i>
              {{ $t('buy.payment.result.retry') }}
            </el-button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import QRCode from 'qrcodejs2'
import { loadStripe } from '@stripe/stripe-js'
import request from '@/utils/http'
import { brandPrefix as getBrandPrefix } from '@/branding'
import { displayCurrencyLabel, formatPrice } from '@/utils/currency'

export default {
  name: 'PaymentStep',
  props: {
    finalAmount: {
      type: Number,
      default: 994
    },
    orderData: {
      type: Object,
      default: () => ({})
    },
    orderNo: {
      type: String,
      default: ''
    },
    purchaseConfig: {
      type: Object,
      default: null
    },
    allowSubscription: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      paymentMode: 'one_time',
      selectedMethod: '',
      paymentPhase: 0, // 0=选择, 1=支付, 2=结果
      paying: false,
      subscriptionLoading: false,
      paymentSuccess: false,
      countdown: 300,
      timer: null,
      orderNumber: '',
      clientSecret: '',
      checkoutSessionId: '',
      alipayPaymentType: 'redirect',
      alipayPaymentUrl: '',
      alipayQrCode: '',
      alipayPollTimer: null,
      stripe: null,
      stripeElements: null,
      paymentTime: '',
      innerSteps: [0, 1, 2],
      wechatQrUrl: '',
      wechatPollTimer: null,
      cardForm: {
        number: '',
        holderName: '',
        expiry: '',
        cvv: ''
      },
      cardRules: {
        number: [
          { required: true, message: '', trigger: 'blur' }
        ],
        holderName: [
          { required: true, message: '', trigger: 'blur' }
        ],
        expiry: [
          { required: true, message: '', trigger: 'blur' }
        ],
        cvv: [
          { required: true, message: '', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    isSubscriptionMode() {
      return this.paymentMode === 'subscription'
    },
    formattedCardNumber() {
      if (!this.cardForm.number) return '•••• •••• •••• ••••'
      return this.cardForm.number.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim()
    },
    phaseTitle() {
      return this.isSubscriptionMode
        ? this.$t('buy.payment.subscription.title')
        : this.$t('buy.payment.selectMethod')
    },
    phaseDescription() {
      return this.isSubscriptionMode
        ? this.$t('buy.payment.subscription.desc')
        : this.$t('buy.payment.oneTime.desc')
    },
    actionLabel() {
      return this.isSubscriptionMode
        ? this.$t('buy.payment.subscription.start')
        : this.$t('buy.payment.proceedToPay')
    },
    resultTitle() {
      return this.isSubscriptionMode
        ? this.$t('buy.payment.result.subscriptionSuccessTitle')
        : this.$t('buy.payment.result.oneTimeSuccessTitle')
    },
    resultDescription() {
      return this.isSubscriptionMode
        ? this.$t('buy.payment.result.subscriptionSuccessDesc')
        : this.$t('buy.payment.result.oneTimeSuccessDesc')
    },
    resultMethodLabel() {
      if (this.isSubscriptionMode) {
        return this.$t('buy.payment.subscription.checkoutMethod')
      }
      return this.selectedMethod ? this.$t(`buy.payment.methods.${this.selectedMethod}.name`) : this.$t('buy.payment.methods.stripe.name')
    },
    resultCurrency() {
      return `${this.displayCurrency} `
    },
    displayCurrency() {
      const raw = (this.purchaseConfig && this.purchaseConfig.displayCurrency) || this.orderData.currency || 'RMB'
      return displayCurrencyLabel(raw)
    },
    brandPrefix() {
      return getBrandPrefix()
    },
    formattedFinalAmount() {
      return formatPrice(this.finalAmount)
    },
    formattedAmount() {
      return `${this.displayCurrency} ${this.formattedFinalAmount}`
    },
    configuredPaymentMethods() {
      const methods = this.purchaseConfig && Array.isArray(this.purchaseConfig.paymentMethods)
        ? this.purchaseConfig.paymentMethods
        : ['card', 'alipay', 'wechat']
      return methods.map(method => String(method).toLowerCase())
    },
    availablePaymentMethods() {
      const methodMap = {
        card: {
          key: 'card',
          logoClass: 'card-network-bg',
          label: this.$t('buy.payment.methods.card.name'),
          desc: this.$t('buy.payment.methods.card.desc'),
          badge: this.$t('buy.payment.methods.card.badge'),
          processor: this.$t('buy.payment.methods.card.processor'),
          flow: this.$t('buy.payment.methods.card.flow')
        },
        alipay: {
          key: 'alipay',
          logoClass: 'alipay-bg',
          image: require('@/assets/img/zfb.png'),
          label: this.$t('buy.payment.methods.alipay.name'),
          desc: this.$t('buy.payment.methods.alipay.desc'),
          badge: this.$t('buy.payment.methods.alipay.badge'),
          processor: this.$t('buy.payment.methods.alipay.processor'),
          flow: this.$t('buy.payment.methods.alipay.flow'),
          recommended: this.configuredPaymentMethods.includes('alipay')
        },
        wechat: {
          key: 'wechat',
          logoClass: 'wechat-bg',
          logoText: 'WX',
          label: this.$t('buy.payment.methods.wechat.name'),
          desc: this.$t('buy.payment.methods.wechat.desc')
        }
      }
      return this.configuredPaymentMethods
        .map(method => methodMap[method === 'wechat_pay' ? 'wechat' : method])
        .filter(method => method && ['card', 'alipay'].includes(method.key))
    },
    stripePublicKey() {
      return (this.purchaseConfig && this.purchaseConfig.stripePublicKey) || 'pk_test_51TKG3LHyTtfBAbnSCT4PhgWwnUarQrv3HYvKUFx7g0K086ZarNWVrn5en7ABwJin7WvIiznKLUmANqImP8q68ABv00Kry2BpuP'
    }
  },
  methods: {
    switchPaymentMode(mode) {
      if (mode === 'subscription' && !this.allowSubscription) {
        return
      }
      this.paymentMode = mode
      this.paymentPhase = 0
      this.paymentSuccess = false
      this.selectedMethod = mode === 'subscription' ? 'card' : ''
      this.alipayPaymentType = 'redirect'
      this.clearAlipayPoll()
    },
    async proceedToPayment() {
      if (this.isSubscriptionMode) {
        return this.startSubscriptionCheckout()
      }
      try {
        this.paying = true
        if (!this.orderNo) {
          throw new Error('Missing orderNo')
        }
        if (this.selectedMethod === 'alipay') {
          const data = await request.post(`/api/payment/orders/${this.orderNo}/alipay`, {
            returnUrl: this.buildAlipayReturnUrl(),
            paymentType: this.alipayPaymentType
          })
          if (this.alipayPaymentType === 'qr' && (!data || !data.qrCode)) {
            throw new Error('Missing Alipay qrCode')
          }
          if (this.alipayPaymentType === 'redirect' && (!data || !data.paymentUrl)) {
            throw new Error('Missing Alipay paymentUrl')
          }
          this.orderNumber = data.orderNo
          this.alipayPaymentUrl = data.paymentUrl || ''
          this.alipayQrCode = data.qrCode || ''
          this.paymentPhase = 1
          this.paying = false
          if (this.alipayPaymentType === 'qr') {
            await this.renderAlipayQR(this.alipayQrCode)
            this.startAlipayQrPoll()
          }
          return
        }

        const data = await request.post(`/api/payment/orders/${this.orderNo}/payment-intent`, {
          paymentMethod: this.selectedMethod
        });

        this.clientSecret = data.clientSecret;
        this.orderNumber = data.orderNo;
        this.paymentPhase = 1;
        this.paying = false;

        if (this.selectedMethod === 'wechat') {
          await this.initWechatPay()
        } else {
          // 初始化 Stripe UI，确保 payment-element 在 DOM 中后再 mount
          this.initStripeElements()
        }
      } catch (err) {
        this.paying = false;
        this.$message.error(this.$t('buy.payment.initError'));
        console.error(err);
      }
    },
    async startSubscriptionCheckout() {
      try {
        this.subscriptionLoading = true
        const payload = {
          successUrl: `${window.location.origin}${window.location.pathname}?checkout_session_id={CHECKOUT_SESSION_ID}&order_no=${this.orderNo}&payment_mode=subscription`,
          cancelUrl: `${window.location.origin}${window.location.pathname}`,
        }
        const data = await request.post(`/api/payment/orders/${this.orderNo}/subscription-session`, payload)
        if (!data || !data.checkoutUrl) {
          throw new Error('Missing checkoutUrl')
        }
        window.location.href = data.checkoutUrl
      } catch (err) {
        console.error(err)
        this.$message.error(this.$t('buy.payment.subscription.checkoutError'))
      } finally {
        this.subscriptionLoading = false
      }
    },
    async initStripeElements() {
      if (!this.stripe) return
      // 等待 Vue DOM 更新 + 外层 slide 过渡动画(350ms) 完成
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 400))

      const el = document.querySelector('#payment-element')
      if (!el) {
        console.error('#payment-element not found in DOM')
          this.$message.error(this.$t('buy.payment.formLoadError'))
        return
      }

      const elements = this.stripe.elements({
        clientSecret: this.clientSecret,
        appearance: {
          theme: 'flat',
          variables: {
            colorPrimary: '#2563eb',
            colorBackground: '#ffffff',
            colorText: '#0f172a',
            colorDanger: '#dc2626',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            borderRadius: '10px',
            spacingUnit: '4px'
          },
          rules: {
            '.Input': {
              border: '1px solid #dbe3ef',
              boxShadow: 'none',
              padding: '13px 14px'
            },
            '.Input:focus': {
              border: '1px solid #2563eb',
              boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)'
            },
            '.Label': {
              color: '#475569',
              fontWeight: '600'
            },
            '.Tab': {
              border: '1px solid #dbe3ef',
              boxShadow: 'none'
            },
            '.Tab--selected': {
              borderColor: '#2563eb',
              boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.10)'
            }
          }
        }
      })
      this.stripeElements = elements
      const paymentElement = elements.create('payment', {
        layout: 'tabs',
        defaultValues: {
           billingDetails: { name: this.cardForm.holderName }
        }
      })
      paymentElement.mount('#payment-element')
    },
    buildAlipayReturnUrl() {
      return `${window.location.origin}/api/payment/alipay/return`
    },
    redirectToAlipay() {
      if (!this.alipayPaymentUrl) {
        this.$message.error(this.$t('buy.payment.initError'))
        return
      }
      this.paying = true
      window.location.href = this.alipayPaymentUrl
    },
    async renderAlipayQR(text) {
      if (!text) return
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 380))

      const container = this.$refs.alipayQrCode
      if (!container) {
        console.error('Alipay QR container not found')
        return
      }

      container.innerHTML = ''
      new QRCode(container, {
        text,
        width: 260,
        height: 260,
        colorDark: 'rgba(10, 127, 215, 0.4)',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.M
      })
    },
    startAlipayQrPoll() {
      this.clearAlipayPoll()
      this.alipayPollTimer = setInterval(async () => {
        try {
          const order = await this.refreshOrderStatus()
          if (order && this.paymentSuccess) {
            this.clearAlipayPoll()
            this.paymentPhase = 2
            this.paymentTime = new Date(order.updatedAt || Date.now()).toLocaleString()
            this.$emit('payment-success', {
              orderId: `${this.brandPrefix}-${this.orderNumber}`,
              method: 'alipay',
              amount: this.finalAmount
            })
          }
        } catch (e) {
          console.error('Alipay QR poll error:', e)
        }
      }, 3000)
    },
    clearAlipayPoll() {
      if (this.alipayPollTimer) {
        clearInterval(this.alipayPollTimer)
        this.alipayPollTimer = null
      }
    },
    async checkAlipayPaymentStatus() {
      try {
        this.paying = true
        const order = await this.refreshOrderStatusWithRetry(2)
        if (order && this.paymentSuccess) {
          this.clearAlipayPoll()
          this.paymentPhase = 2
          this.$emit('payment-success', {
            orderId: `${this.brandPrefix}-${this.orderNumber}`,
            method: 'alipay',
            amount: this.finalAmount
          })
          return
        }
        this.$message.info(this.$t('buy.payment.alipayMode.qr.pending'))
      } catch (e) {
        console.error(e)
        this.$message.error(this.$t('buy.payment.initError'))
      } finally {
        this.paying = false
      }
    },
    async initWechatPay() {
      if (!this.stripe || !this.clientSecret) return

      const { paymentIntent, error } = await this.stripe.confirmWechatPayPayment(
        this.clientSecret,
        {
          payment_method: {
            billing_details: {
              name: this.cardForm.holderName || 'Customer'
            }
          },
          // 用户扫码完成后 Stripe 会重定向回到此 URL
          return_url: window.location.origin + window.location.pathname + '?payment_intent_client_secret=' + this.clientSecret + '&order_no=' + this.orderNo
        }
      )

      if (error) {
        this.$message.error(error.message);
        this.paying = false;
        return;
      }

      // 没有发生重定向（桌面端需要扫码），渲染二维码
      this.paying = false;
      if (paymentIntent && paymentIntent.next_action && paymentIntent.next_action.wechat_pay_display_qr_code) {
        let qrUrl = ''
        const qrCode = paymentIntent.next_action.wechat_pay_display_qr_code
        // 新 API 格式: { qr_code_url: 'https://...' }
        if (qrCode.qr_code_url) {
          qrUrl = qrCode.qr_code_url
        }
        // 旧 API 格式: [{ url: 'https://...' }] 数组形式
        else if (Array.isArray(qrCode) && qrCode.length > 0) {
          qrUrl = qrCode[0].url || qrCode[0]
        }
        if (qrUrl) {
          this.renderWechatQR(qrUrl)
          this.wechatQrUrl = qrUrl
        }
      }

      // 开始轮询支付状态
      this.startWechatPayPoll()
    },
    renderWechatQR(text) {
      this.$nextTick(() => {
        const container = this.$refs.wechatQrCode
        if (!container) return
        // 清除旧的二维码
        container.innerHTML = ''
        const qrcode = new QRCode(container, {
          text: text,
          width: 200,
          height: 200,
          colorDark: '#07c160',
          colorLight: '#ffffff',
          correctLevel: QRCode.CorrectLevel.H
        })
      })
    },
    startWechatPayPoll() {
      // 每 3 秒轮询一次 PaymentIntent 状态，最多 300 次 (15 分钟)
      this.wechatPollTimer = setInterval(async () => {
        if (!this.stripe || !this.clientSecret) return
        try {
          const { paymentIntent, error: retrieveError } =
            await this.stripe.retrievePaymentIntent(this.clientSecret)

          if (retrieveError) return

          if (paymentIntent && paymentIntent.status === 'succeeded') {
            clearInterval(this.wechatPollTimer)
            this.wechatPollTimer = null
            await this.refreshOrderStatus()
            this.paymentPhase = 2
            this.paymentSuccess = true
            this.paymentTime = new Date().toLocaleString()
            this.$emit('payment-success', {
              orderId: `${this.brandPrefix}-${this.orderNumber}`,
              method: 'wechat',
              amount: this.finalAmount
            })
          }
        } catch (e) {
          console.error('WeChat pay poll error:', e)
        }
      }, 3000)
    },
    async handleCardPayment() {
      this.paying = true
      
      if (!this.stripe || !this.stripeElements) {
         this.paying = false;
         return;
      }
      
      const { error } = await this.stripe.confirmPayment({
        elements: this.stripeElements,
        confirmParams: {
          return_url: window.location.origin + window.location.pathname + '?payment_intent_client_secret=' + this.clientSecret + '&order_no=' + this.orderNo,
        },
        redirect: 'if_required' // 尽量避免跳转，除非 3D Secure 要求
      });

      this.paying = false;

      if (error) {
        this.$message.error(error.message || this.$t('buy.payment.cardFailed'));
        this.completePayment(false);
      } else {
        this.completePayment(true);
      }
    },
    completePayment(success) {
      if (this.timer) clearInterval(this.timer)
      this.clearAlipayPoll()
      this.paymentSuccess = success
      this.paymentTime = new Date().toLocaleString()
      this.paymentPhase = 2
      
      if (success) {
        this.refreshOrderStatus()
        this.$emit('payment-success', {
          orderId: `${this.brandPrefix}-${this.orderNumber}`,
          method: this.selectedMethod,
          amount: this.finalAmount
        })
      }
    },
    retryPayment() {
      this.paymentPhase = 0
      this.selectedMethod = ''
      this.clearAlipayPoll()
    },
    returnToPaymentMethods() {
      this.paymentPhase = 0
      this.clearAlipayPoll()
    },
    downloadPageShortcut() {
      const shortcutUrl = this.$brand.shortcutUrl || `${window.location.origin}/`
      const shortcutName = this.$t('buy.payment.result.shortcutFileName')
      const logoUrl = `${window.location.origin}${this.$brand.logo}`
      const shortcutContent = `[InternetShortcut]\nURL=${shortcutUrl}\nIconFile=${logoUrl}\nIconIndex=0\n`
      const blob = new Blob([shortcutContent], { type: 'text/plain;charset=utf-8' })
      const downloadLink = document.createElement('a')
      downloadLink.href = URL.createObjectURL(blob)
      downloadLink.download = `${shortcutName}.url`
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      URL.revokeObjectURL(downloadLink.href)
      this.$message.success(this.$t('buy.payment.result.shortcutDownloaded'))
    },
    viewInvoice() {
      const invoicePayload = {
        orderId: `${this.brandPrefix}-${this.orderNumber}`,
        method: this.selectedMethod,
        amount: this.finalAmount,
        paymentTime: this.paymentTime
      }
      this.$emit('view-invoice', invoicePayload)
      if (this.$brand && this.$brand.id === 'lpt') {
        this.$router.push({
          path: '/controlCenter',
          query: {
            tab: 'accountInfo'
          }
        })
        return
      }
      this.$router.push({
        path: '/buy/invoice',
        query: {
          orderNo: this.orderNo || this.orderNumber
        }
      })
    },
    async refreshOrderStatus() {
      if (!this.orderNo && !this.orderNumber) return null
      const order = await request.get(`/api/payment/orders/${this.orderNo || this.orderNumber}`)
      this.orderNumber = order.orderNo
      this.paymentSuccess = ['SUCCESS', 'ACTIVE'].includes(String(order.status).toUpperCase())
      this.paymentTime = new Date(order.updatedAt || Date.now()).toLocaleString()
      return order
    },
    async refreshOrderStatusWithRetry(maxAttempts = 6) {
      let order = null
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        order = await this.refreshOrderStatus()
        if (this.paymentSuccess || attempt === maxAttempts - 1) {
          return order
        }
        await new Promise(resolve => setTimeout(resolve, 1500))
      }
      return order
    }
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
    if (this.wechatPollTimer) clearInterval(this.wechatPollTimer)
    this.clearAlipayPoll()
  },
  async created() {
    this.$emit('validate', true)
    if (!this.allowSubscription) {
      this.paymentMode = 'one_time'
    }
    if (!this.isSubscriptionMode && this.availablePaymentMethods.length === 1) {
      this.selectedMethod = this.availablePaymentMethods[0].key
    }
    
    // 初始化 Stripe 实例，使用从后端拿到的由我们部署写入的 Publishable Key
    this.stripe = await loadStripe(this.stripePublicKey);
    
    // 检查是否是从 Stripe 重定向回来的（解决 Web 端 Alipay、WeChat 和 3D-Secure 问题）
    const urlParams = new URLSearchParams(window.location.search);
    const clientSecretCallback = urlParams.get('payment_intent_client_secret');
    const checkoutSessionCallback = urlParams.get('checkout_session_id');
    const urlPaymentMode = urlParams.get('payment_mode');
    const paymentMethodCallback = urlParams.get('payment_method');
    const isAlipayReturn = urlParams.get('alipay_return') === '1' || paymentMethodCallback === 'alipay';
    const orderNoCallback = urlParams.get('order_no') || this.orderNo;

    if (urlPaymentMode === 'subscription') {
      this.paymentMode = 'subscription'
      this.selectedMethod = 'card'
    }
    
    if (isAlipayReturn && orderNoCallback) {
      try {
        this.selectedMethod = 'alipay'
        this.orderNumber = orderNoCallback
        this.paymentPhase = 2
        await this.refreshOrderStatusWithRetry()
        if (this.paymentSuccess) {
          this.$emit('payment-success', {
            orderId: `${this.brandPrefix}-${this.orderNumber}`,
            method: 'alipay',
            amount: this.finalAmount
          })
        }
      } catch (e) {
        console.error('获取支付宝支付结果失败', e)
      }
    } else if (clientSecretCallback && this.stripe) {
      try {
        const { paymentIntent } = await this.stripe.retrievePaymentIntent(clientSecretCallback);
        this.paymentPhase = 2; // 直接跳到结果页
        this.paymentSuccess = paymentIntent && paymentIntent.status === 'succeeded';
        if (paymentIntent) {
           this.orderNumber = orderNoCallback || this.orderNo || paymentIntent.id.slice(-8);
           this.paymentTime = new Date().toLocaleString();
           if (orderNoCallback) {
             await this.refreshOrderStatus()
           }
           if (this.paymentSuccess) {
              this.$emit('payment-success', {
                orderId: `${this.brandPrefix}-${this.orderNumber}`,
                method: paymentIntent.payment_method_types[0] || 'stripe',
                amount: this.finalAmount
              });
           }
        }
      } catch (e) {
        console.error('获取结果失败', e);
      }
    } else if (checkoutSessionCallback) {
      try {
        this.checkoutSessionId = checkoutSessionCallback
        const data = await request.get(`/api/payment/subscription-session/${checkoutSessionCallback}`)
        this.paymentPhase = 2
        this.paymentMode = 'subscription'
        this.paymentSuccess = ['paid', 'complete', 'active', 'ACTIVE'].includes(String(data.paymentStatus || data.status).toLowerCase())
        this.orderNumber = data.orderNo || orderNoCallback || checkoutSessionCallback.slice(-8)
        if (this.orderNumber) {
          await this.refreshOrderStatus()
        }
        this.paymentTime = new Date().toLocaleString()
      } catch (e) {
        console.error('获取订阅结果失败', e)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.payment-step {
  max-width: 720px;
  margin: 0 auto;
  position: relative;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 20px;
  background: linear-gradient(135deg, #6366f1, #818cf8);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 24px;
  i {
    font-size: 14px;
  }
}

.payment-mode-switch {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 24px;
}

.mode-card {
  padding: 18px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  background: linear-gradient(180deg, #fff, #f8fafc);
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);

  &:hover {
    border-color: #c7d2fe;
    transform: translateY(-1px);
  }

  &.active {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
    background: linear-gradient(180deg, #eef2ff, #fff);
  }
}

.mode-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.mode-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.mode-desc {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.55;
}

// Inner Steps
.payment-inner-steps {
  display: flex;
  justify-content: center;
  gap: 50px;
  margin-bottom: 40px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 16px;
    left: 25%;
    right: 25%;
    height: 2px;
    background: #e2e8f0;
    z-index: 0;
  }

  .inner-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    position: relative;
    z-index: 1;

    .inner-step-dot {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      border: 2px solid #e2e8f0;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 600;
      color: #94a3b8;
      transition: all 0.4s;
    }

    .inner-step-label {
      font-size: 13px;
      color: #94a3b8;
      font-weight: 500;
      transition: all 0.3s;
    }

    &.active {
      .inner-step-dot {
        border-color: #6366f1;
        background: #6366f1;
        color: white;
        box-shadow: 0 4px 12px rgba(99,102,241,0.3);
      }
      .inner-step-label {
        color: #6366f1;
        font-weight: 600;
      }
    }

    &.done {
      .inner-step-dot {
        border-color: #10b981;
        background: #10b981;
        color: white;
      }
      .inner-step-label {
        color: #10b981;
      }
    }
  }
}

// Phase header
.phase-header {
  text-align: center;
  margin-bottom: 30px;

  h3 {
    font-size: 22px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 16px;
  }

  .phase-desc {
    margin: -4px 0 14px;
    color: #64748b;
    font-size: 14px;
  }
}

.amount-banner {
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  padding: 12px 28px;
  background: linear-gradient(135deg, #f0f4ff, #ede9fe);
  border-radius: 12px;

  .amount-label {
    font-size: 14px;
    color: #475569;
  }

  .amount-value {
    font-size: 32px;
    font-weight: 800;
    color: #6366f1;
    letter-spacing: -1px;
  }
}

.subscription-panel {
  margin-bottom: 28px;
}

.subscription-card {
  border: 1.5px solid #c7d2fe;
  border-radius: 16px;
  background: linear-gradient(180deg, #eef2ff 0%, #fff 100%);
  padding: 18px 20px;
}

.subscription-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  color: #4338ca;
  margin-bottom: 14px;

  i {
    color: #6366f1;
  }
}

.subscription-card-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.subscription-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #334155;
  font-size: 14px;

  strong {
    color: #111827;
  }
}

// Methods Grid
.methods-grid {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 30px;
}




.method-card {
  display: flex;
  align-items: center;
  gap: 18px;
  min-height: 118px;
  padding: 20px 178px 20px 24px;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fff;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
  position: relative;
  overflow: hidden;
  text-align: left;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  outline: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: #cbd5e1;
  }

  .method-radio,
  .method-logo,
  .method-info,
  .method-badge {
    position: relative;
    z-index: 1;
  }

  &:hover {
    border-color: #c7d2fe;
    box-shadow: 0 10px 26px rgba(99, 102, 241, 0.11);
    transform: translateY(-1px);
  }

  &.selected {
    border-color: #6366f1;
    background: linear-gradient(135deg, #f8faff 0%, #fff 100%);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1), 0 12px 28px rgba(99, 102, 241, 0.12);
  }
}

.payment-method-card {
  &::before {
    background: #1a1f71;
  }

  &.selected {
    background: linear-gradient(135deg, #f7f9ff 0%, #fff 100%);
  }
}

.payment-method-alipay {
  &::before {
    background: #1677ff;
  }

  &.selected {
    background: linear-gradient(135deg, #f2f8ff 0%, #fff 100%);
  }
}

.method-title-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 4px;
}

.method-channel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: absolute;
  top: 20px;
  right: 24px;
  z-index: 2;
  min-width: 118px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.method-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 10px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #475569;
    font-size: 12px;
    line-height: 1.4;
  }

  em {
    color: #94a3b8;
    font-style: normal;
    font-weight: 600;
  }
}

.method-radio {
  .radio-dot {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid #cbd5e1;
    transition: all 0.3s;
    position: relative;

    &.checked {
      border-color: #6366f1;

      &::after {
        content: '';
        position: absolute;
        top: 4px;
        left: 4px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #6366f1;
      }
    }
  }
}

.method-logo {
  width: 72px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 38px;
    height: 38px;
    object-fit: contain;
  }

  &.alipay-bg {
    background: #e8f5ff;
  }
  &.card-network-bg {
    width: 86px;
    background: #f8fafc;
    border: 1px solid #dbe3ef;
  }
  &.mastercard-bg {
    background: #f5f0eb;
  }
  &.wechat-bg {
    background: #e8f8e0;
  }
  .wechat-text {
    font-size: 26px;
  }
}

.visa-text {
  font-size: 12px;
  font-weight: 800;
  color: white;
  letter-spacing: 1px;
}

.card-network-stack {
  display: flex;
  align-items: center;
  gap: 6px;
}

.network-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.visa-mark {
  min-width: 34px;
  height: 22px;
  padding: 0 5px;
  border-radius: 4px;
  background: #1a1f71;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  font-style: italic;
  letter-spacing: 0.4px;
}

.mastercard-mark {
  width: 34px;
  height: 22px;
  border-radius: 4px;
  background: #f8fafc;
  border: 1px solid #dbe3ef;
}

.mc-disc {
  display: inline-block;
  width: 17px;
  height: 17px;
  border-radius: 50%;

  &.mc-red {
    background: #eb001b;
    margin-right: -6px;
    opacity: 0.92;
  }

  &.mc-yellow {
    background: #f79e1b;
    opacity: 0.92;
  }
}

.method-info {
  flex: 1;
  min-width: 0;

  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 4px;
  }

  p {
    font-size: 13px;
    color: #94a3b8;
    margin: 0;
    line-height: 1.45;
  }
}

.method-badge {
  position: absolute;
  top: 50px;
  right: 24px;
  z-index: 2;
}

.phase-actions {
  text-align: center;
}

.alipay-mode-panel {
  margin: 18px auto 0;
  padding: 18px;
  max-width: 760px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: #f8fbff;
}

.alipay-mode-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;

  span {
    color: #1e293b;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    color: #64748b;
    font-size: 12px;
  }
}

.alipay-mode-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.alipay-mode-option {
  min-height: 92px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #334155;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-bottom: 10px;
    border-radius: 8px;
    background: #eff6ff;
    color: #1677ff;
    font-size: 16px;
  }

  span,
  em {
    display: block;
  }

  span {
    margin-bottom: 4px;
    color: #1e293b;
    font-size: 14px;
    font-weight: 700;
  }

  em {
    color: #64748b;
    font-size: 12px;
    font-style: normal;
    line-height: 1.45;
  }

  &:hover,
  &.active {
    border-color: #1677ff;
    box-shadow: 0 8px 22px rgba(22, 119, 255, 0.12);
  }

  &.active {
    background: #f2f8ff;
  }
}

.proceed-btn {
  padding: 14px 40px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  background: linear-gradient(135deg, #6366f1, #818cf8);
  border: none;

  &:hover:not(:disabled) {
    box-shadow: 0 8px 24px rgba(99,102,241,0.35);
    transform: translateY(-2px);
  }

  i {
    margin-left: 6px;
  }
}

// QR Payment
.qr-payment {
  text-align: center;
}

.qr-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 30px;

  .qr-method-icon {
    width: 36px;
    height: 36px;
    object-fit: contain;
  }

  h3 {
    font-size: 22px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
  }
}

.qr-body {
  margin-bottom: 30px;
}

.qr-checkout-panel {
  width: min(100%, 380px);
  margin: 0 auto;
  padding: 16px;
  border: 1px solid #dbeafe;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 52%);
  box-shadow: 0 16px 36px rgba(10, 127, 215, 0.10);
}

.qr-order-no {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  margin: 0 0 14px;
  border: 1px solid rgba(10, 127, 215, 0.14);
  border-radius: 12px;
  background: #ffffff;
  color: #64748b;
  font-size: 12px;
  line-height: 1.3;
  text-align: left;

  strong {
    color: #0f172a;
    font-weight: 700;
    letter-spacing: 0.3px;
    font-size: 13px;
    word-break: break-all;
    text-align: right;
  }
}

.qr-code-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #eef2f7;
}

.qr-code-wrapper {
  position: relative;
  display: inline-block;
  padding: 14px;
  background: #fff;
  border: 0;
  border-radius: 12px;
  box-shadow: none;

  .scan-line {
    position: absolute;
    top: 14px;
    left: 14px;
    right: 14px;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(10, 127, 215, 0.45), transparent);
    animation: scanAnimation 2.5s ease-in-out infinite;
    border-radius: 2px;
  }
}

.alipay-qr-wrapper {
  .alipay-qr-code {
    display: inline-block;
    margin: 0 auto;

    ::v-deep img,
    ::v-deep canvas {
      display: block;
    }
  }
}

@keyframes scanAnimation {
  0%, 100% { top: 14px; }
  50% { top: calc(100% - 16px); }
}

.qr-tip {
  font-size: 13px;
  color: #64748b;
  margin: 14px 0 0;
  line-height: 1.5;
}

// WeChat QR Code
.qr-code-container {
  display: inline-block;
  padding: 20px;
  background: #fff;
  border: 2px solid #e8f8e0;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(16,185,129,0.08);

  .wechat-qr-code {
    display: inline-block;
    margin: 0 auto;

    ::v-deep img {
      display: block;
    }
    ::v-deep canvas {
      display: block;
    }
  }
}

.wechat-payment .qr-header {
  .wechat-icon-qr {
    font-size: 28px;
  }
}

.countdown-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 20px;
  font-size: 14px;
  color: #92400e;

  i {
    font-size: 16px;
    color: #f59e0b;
  }
}

.qr-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.simulate-btn {
  padding: 12px 36px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  background: linear-gradient(135deg, #1677ff, #4096ff);
  border: none;

  i {
    margin-right: 6px;
  }
}

.back-btn {
  border: none;
  color: #6366f1;
  background: none;
  font-weight: 500;

  &:hover {
    color: #4f46e5;
    background: #f5f3ff;
  }
}

@media (max-width: 640px) {
  .payment-step {
    max-width: 100%;
  }

  .step-badge {
    padding: 5px 12px;
    margin-bottom: 16px;
    font-size: 12px;
  }

  .payment-mode-switch {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 18px;
  }

  .mode-card {
    padding: 14px;
    border-width: 1px;
    border-radius: 12px;
    box-shadow: none;

    &:hover {
      transform: none;
    }
  }

  .mode-top {
    align-items: flex-start;
  }

  .payment-inner-steps {
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 26px;

    &::before {
      left: 18%;
      right: 18%;
    }

    .inner-step {
      flex: 1;

      .inner-step-dot {
        width: 30px;
        height: 30px;
        font-size: 12px;
      }

      .inner-step-label {
        font-size: 12px;
        text-align: center;
        line-height: 1.3;
      }
    }
  }

  .phase-header {
    text-align: left;
    margin-bottom: 20px;

    h3 {
      font-size: 20px;
      margin-bottom: 10px;
    }
  }

  .amount-banner {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 14px 16px;
    background: linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%);
    border: 1px solid #e0e7ff;
    border-radius: 12px;
    box-sizing: border-box;

    .amount-label {
      font-size: 13px;
      color: #64748b;
    }

    .amount-value {
      font-size: 26px;
      font-weight: 800;
      color: #1d4ed8;
      letter-spacing: -0.5px;
      word-break: break-word;
    }
  }

  .method-card {
    align-items: flex-start;
    gap: 12px;
    min-height: 0;
    padding: 14px;
    border-width: 1px;
    border-radius: 12px;
    box-shadow: none;

    &:hover {
      transform: none;
    }
  }

  .method-logo {
    width: 46px;
    height: 34px;

    img {
      width: 30px;
      height: 30px;
    }
  }

  .method-logo.card-network-bg {
    width: 74px;
  }

  .method-title-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .method-channel {
    position: static;
    justify-content: flex-start;
    min-width: 0;
  }

  .method-badge {
    position: absolute;
    top: 12px;
    right: 12px;
  }

  .alipay-mode-panel {
    padding: 14px;
  }

  .alipay-mode-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .alipay-mode-options {
    grid-template-columns: 1fr;
  }

  .proceed-btn,
  .simulate-btn,
  .pay-now-btn {
    width: 100%;
    padding-left: 12px;
    padding-right: 12px;
  }

  .qr-header,
  .card-header {
    align-items: flex-start;
    justify-content: flex-start;
    margin-bottom: 20px;
    text-align: left;

    h3 {
      font-size: 20px;
    }
  }

  .qr-code-wrapper,
  .qr-code-container {
    width: 100%;
    max-width: 100%;
    padding: 14px;
    box-sizing: border-box;
  }

  .qr-checkout-panel {
    width: 100%;
    padding: 14px;
    box-sizing: border-box;
  }

  .qr-order-no {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;

    strong {
      text-align: left;
    }
  }

  .alipay-qr-wrapper .alipay-qr-code,
  .qr-code-container .wechat-qr-code {
    max-width: 100%;
    overflow: hidden;

    ::v-deep img,
    ::v-deep canvas {
      max-width: 100%;
      height: auto !important;
    }
  }

  .card-payment,
  .card-form-wrapper {
    max-width: 100%;
  }

  .card-checkout-shell {
    padding: 16px;
  }

  .card-checkout-top {
    align-items: flex-start;
    flex-direction: column;
  }

  .accepted-card-list {
    width: 100%;
  }

  .credit-card-preview {
    width: 100%;
    max-width: 340px;
    height: auto;
    min-height: 190px;
    padding: 22px;
    box-sizing: border-box;
  }

  .card-form .form-row {
    flex-direction: column;
    gap: 0;
  }

  .payment-result {
    padding: 24px 0;
  }

  .result-title {
    font-size: 22px;
  }

  .result-details {
    max-width: 100%;
    padding: 14px;

    .result-detail-row {
      align-items: flex-start;
      flex-direction: column;
      gap: 4px;
    }
  }

  .result-actions {
    flex-direction: column;

    .shortcut-btn,
    .invoice-btn {
      width: 100%;
      padding-left: 12px;
      padding-right: 12px;
    }
  }
}

// Card Payment
.card-payment {
  max-width: 560px;
  margin: 0 auto;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 30px;

  .card-method-icon {
    min-width: 88px;
    height: 38px;
    padding: 0 7px;
    border-radius: 8px;
    border: 1px solid #dbe3ef;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
  }

  h3 {
    font-size: 22px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
  }
}

// Credit Card Preview
.credit-card-preview {
  width: 380px;
  height: 220px;
  border-radius: 16px;
  padding: 28px;
  margin: 0 auto 30px;
  position: relative;
  color: white;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(0,0,0,0.2);

  &.visa {
    background: linear-gradient(135deg, #1a1f71 0%, #2d3a8c 50%, #4050b0 100%);
  }
  &.mastercard {
    background: linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%);
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -30%;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: rgba(255,255,255,0.05);
  }

  .card-chip {
    width: 46px;
    height: 34px;
    border-radius: 6px;
    background: linear-gradient(135deg, #e0c97f, #d4a24e);
    margin-bottom: 24px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 4px;
      right: 4px;
      height: 1px;
      background: rgba(0,0,0,0.15);
    }

    &::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 4px;
      bottom: 4px;
      width: 1px;
      background: rgba(0,0,0,0.15);
    }
  }

  .card-number-display {
    font-size: 22px;
    font-weight: 500;
    letter-spacing: 3px;
    margin-bottom: 24px;
    font-family: 'Courier New', monospace;
  }

  .card-bottom {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .card-label {
    display: block;
    font-size: 12px;
    opacity: 0.6;
    letter-spacing: 1px;
    margin-bottom: 2px;
    text-transform: uppercase;
  }

  .card-value {
    display: block;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .card-brand {
    position: absolute;
    top: 24px;
    right: 28px;

    .visa-brand {
      font-size: 28px;
      font-weight: 800;
      font-style: italic;
      letter-spacing: 2px;
    }

    .mc-brand {
      display: flex;
      .mc-circle {
        width: 30px;
        height: 30px;
        border-radius: 50%;

        &.mc-red {
          background: #eb001b;
          margin-right: -10px;
          opacity: 0.9;
        }
        &.mc-yellow {
          background: #f79e1b;
          opacity: 0.9;
        }
      }
    }
  }
}

// Card Form
.card-form-wrapper {
  margin-bottom: 20px;
}

.card-checkout-shell {
  padding: 20px;
  margin-bottom: 18px;
  border: 1px solid #dbe3ef;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
}

.card-checkout-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  h4 {
    margin: 4px 0 0;
    color: #0f172a;
    font-size: 18px;
    font-weight: 750;
  }
}

.card-checkout-kicker {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.accepted-card-list {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
}

.brand-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid #dbe3ef;
  border-radius: 6px;
  background: #ffffff;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.2px;
}

.visa-pill {
  background: #1a1f71;
  border-color: #1a1f71;
  color: #ffffff;
  font-style: italic;
}

.mastercard-pill {
  gap: 0;
  background: #ffffff;
}

.card-amount-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 18px;
  border-radius: 10px;
  background: #eff6ff;
  color: #475569;
  font-size: 13px;

  strong {
    color: #1d4ed8;
    font-size: 18px;
    font-weight: 800;
  }
}

.card-input-label {
  margin-bottom: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}

.stripe-payment-element {
  position: relative;
  z-index: 1;
  min-height: 150px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  cursor: text;
}

.card-form-helper {
  margin: 10px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.card-form {
  .form-row {
    display: flex;
    gap: 16px;
  }

  .half-width {
    flex: 1;
  }

  ::v-deep .el-form-item__label {
    font-weight: 600;
    color: #334155;
    font-size: 14px;
    padding-bottom: 6px;
  }

  ::v-deep .el-input__inner {
    border-radius: 10px;
    padding: 12px 14px 12px 38px;
    font-size: 15px;
    border: 1.5px solid #e2e8f0;
    transition: all 0.3s;

    &:focus {
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
    }
  }

  ::v-deep .el-input__prefix {
    left: 12px;
    color: #94a3b8;
  }
}

.card-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.pay-now-btn {
  width: 100%;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  background: linear-gradient(135deg, #10b981, #34d399);
  border: none;
  
  &:hover:not(:disabled) {
    box-shadow: 0 8px 24px rgba(16,185,129,0.3);
    transform: translateY(-1px);
  }

  i {
    margin-right: 6px;
  }
}

.card-security-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  font-size: 13px;
  color: #94a3b8;

  i {
    color: #10b981;
  }
}

// Payment Result
.payment-result {
  text-align: center;
  padding: 40px 20px;

  .result-icon-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;

    i {
      font-size: 40px;
      color: white;
    }

    &.success-icon {
      background: linear-gradient(135deg, #10b981, #34d399);
      box-shadow: 0 12px 32px rgba(16,185,129,0.3);
      animation: resultPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    &.failed-icon {
      background: linear-gradient(135deg, #ef4444, #f87171);
      box-shadow: 0 12px 32px rgba(239,68,68,0.3);
    }
  }
}

@keyframes resultPop {
  0% { transform: scale(0); }
  100% { transform: scale(1); }
}

.result-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px;
}

.result-subtitle {
  font-size: 15px;
  color: #64748b;
  margin: 0 0 32px;
}

.result-details {
  max-width: 400px;
  margin: 0 auto 32px;
  background: #f8fafc;
  border-radius: 14px;
  padding: 20px;

  .result-detail-row {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #e2e8f0;
    font-size: 14px;
    color: #64748b;

    &:last-child {
      border-bottom: none;
    }

    .detail-value {
      font-weight: 600;
      color: #1e293b;
    }
  }
}

.result-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.shortcut-btn,
.invoice-btn {
  padding: 14px 40px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;

  i {
    margin-right: 6px;
  }
}

.invoice-btn {
  background: linear-gradient(135deg, #6366f1, #818cf8);
  border: none;

  &:hover {
    box-shadow: 0 8px 24px rgba(99,102,241,0.35);
    transform: translateY(-2px);
  }
}

.retry-btn {
  padding: 14px 40px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;

  i {
    margin-right: 6px;
  }
}

// Transition
.pay-fade-enter-active,
.pay-fade-leave-active {
  transition: all 0.35s ease;
}

.pay-fade-enter {
  opacity: 0;
  transform: translateY(20px);
}

.pay-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

// Payment redesign overrides v2
.payment-step {
  max-width: 920px;
  color: #0f172a;
}

.step-badge {
  padding: 5px 12px;
  margin-bottom: 18px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  box-shadow: none;
  font-size: 12px;
}

.payment-mode-switch {
  max-width: 620px;
  margin: 0 auto 22px;
  padding: 4px;
  gap: 4px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
}

.mode-card {
  padding: 14px 16px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  box-shadow: none;

  &:hover {
    transform: none;
    box-shadow: none;
    background: #ffffff;
  }

  &.active {
    border: 0;
    background: #ffffff;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  }
}

.mode-title,
.phase-header h3,
.qr-header h3,
.card-header h3 {
  color: #0f172a;
  letter-spacing: 0;
}

.payment-inner-steps {
  max-width: 520px;
  margin: 0 auto 22px;
  padding: 6px 0;

  &::before {
    background: #dbe3ef;
  }

  .inner-step {
    .inner-step-dot {
      border-color: #dbe3ef;
      color: #64748b;
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
    }

    &.active {
      .inner-step-dot {
        border-color: #2563eb;
        background: #2563eb;
        box-shadow: 0 8px 20px rgba(37, 99, 235, 0.22);
      }

      .inner-step-label {
        color: #1d4ed8;
      }
    }

    &.done {
      .inner-step-dot {
        border-color: #0f766e;
        background: #0f766e;
      }

      .inner-step-label {
        color: #0f766e;
      }
    }
  }
}

.payment-phase {
  padding: 0;
  border: 1px solid #dbe3ef;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 18px 46px rgba(15, 23, 42, 0.07);
  overflow: hidden;
}

.phase-header {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 18px;
  padding: 24px 26px;
  margin-bottom: 0;
  border-bottom: 1px solid #eef2f7;
  background: #fbfdff;
  text-align: left;

  h3 {
    margin-bottom: 8px;
    font-size: 24px;
  }

  .phase-desc {
    margin: 0;
    color: #64748b;
  }
}

.amount-banner {
  min-width: 210px;
  justify-content: space-between;
  padding: 10px 0 0;
  background: transparent;
  border: 0;
  border-radius: 0;

  .amount-label {
    color: #64748b;
  }

  .amount-value {
    color: #1d4ed8;
    font-size: 28px;
    letter-spacing: 0;
  }
}

.methods-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  padding: 24px 26px 10px;
  margin-bottom: 0;
}

.method-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-height: 190px;
  padding: 18px;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: none;
  flex-wrap: wrap;

  &::before {
    display: none;
  }

  &:hover {
    border-color: #93c5fd;
    box-shadow: 0 10px 24px rgba(37, 99, 235, 0.08);
  }

  &.selected {
    border-color: #2563eb;
    background: #fbfdff;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.10);
  }
}

.method-radio {
  padding-top: 3px;
}

.method-logo {
  order: 2;
  width: 100%;
  height: 46px;
  margin-top: auto;
  border-radius: 10px;
  justify-content: flex-start;
  padding-left: 0;
}

.method-logo.card-network-bg {
  width: 100%;
  background: transparent;
  border: 0;
}

.method-title-row {
  display: block;
  margin-bottom: 6px;
}

.method-info {
  flex: 1;
  padding-right: 0;
  min-width: 0;

  h4 {
    color: #0f172a;
    font-size: 18px;
    font-weight: 750;
  }

  p {
    max-width: 100%;
    color: #64748b;
  }
}

.method-channel {
  position: static;
  order: 3;
  justify-content: flex-start;
  min-width: 0;
  padding: 0;
  margin-top: 8px;
  background: transparent;
  color: #1d4ed8;
  border: 0;
  border-radius: 0;
  font-size: 12px;
  font-weight: 750;
}

.method-meta {
  order: 4;
  width: 100%;
  margin-top: 6px;
  padding-top: 12px;
  border-top: 1px solid #eef2f7;

  span {
    color: #475569;
  }
}

.method-badge {
  top: 14px;
  right: 14px;
}

.phase-actions {
  display: flex;
  justify-content: flex-end;
  padding: 10px 26px 26px;
}

.proceed-btn {
  min-width: 220px;
  background: #2563eb;
  border-radius: 10px;

  &:hover:not(:disabled) {
    background: #1d4ed8;
    box-shadow: 0 10px 24px rgba(37, 99, 235, 0.24);
  }
}

.card-payment {
  max-width: none;
  padding: 24px 26px 26px;
}

.card-header {
  justify-content: space-between;
  padding: 0 2px;
  margin-bottom: 18px;

  .card-method-icon {
    order: 2;
    min-width: 98px;
  }
}

.card-form-wrapper {
  margin-bottom: 16px;
}

.card-checkout-shell {
  padding: 18px;
  border-radius: 14px;
  box-shadow: none;
  background: #fbfdff;
}

.stripe-payment-element {
  border-color: #dbe3ef;
}

.pay-now-btn {
  background: #2563eb;
  border-radius: 10px;

  &:hover:not(:disabled) {
    background: #1d4ed8;
    box-shadow: 0 10px 24px rgba(37, 99, 235, 0.24);
  }
}

.card-security-note {
  margin-top: 4px;
  border-radius: 10px;
  background: #f8fafc;
  color: #64748b;

  i {
    color: #0f766e;
  }
}

@media (max-width: 640px) {
  .payment-phase {
    border-radius: 14px;
    box-shadow: none;
  }

  .phase-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    padding: 18px;
  }

  .amount-banner {
    min-width: 0;
  }

  .method-card {
    min-height: 0;
    padding: 14px;
  }

  .credit-card-preview {
    width: 100%;
    max-width: 380px;
    height: auto;
    aspect-ratio: 19 / 11;
    padding: 24px 20px;
  }

  .methods-grid {
    grid-template-columns: 1fr;
    padding: 18px 18px 8px;
  }

  .method-logo {
    display: flex;
  }

  .method-badge {
    top: 14px;
    right: 14px;
  }

  .method-meta {
    flex-direction: column;
  }

  .phase-actions,
  .proceed-btn {
    width: 100%;
  }

  .phase-actions {
    padding: 8px 18px 18px;
  }

  .card-header {
    flex-direction: column;
    gap: 10px;

    .card-method-icon {
      order: 0;
    }
  }

  .card-payment {
    padding: 18px;
  }
}
</style>
